import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { currentLocale } from '../../i18n';
import './Tournaments.css';

// Status labels are translated; keys resolved via t() at render time
// (module-level constant can't call hooks).
const STATUS_LABELS = {
  draft: 'tournament.statusDraft',
  scheduled: 'tournament.statusScheduled',
  registration_open: 'tournament.statusRegOpen',
  registration_closed: 'tournament.statusRegClosed',
  in_progress: 'tournament.statusInProgress',
  completed: 'tournament.statusCompleted',
  cancelled: 'tournament.statusCancelled',
};

const STATUS_COLORS = {
  draft: 'var(--text-muted)',
  scheduled: 'var(--accent-blue)',
  registration_open: 'var(--accent-green)',
  registration_closed: 'var(--accent-orange)',
  in_progress: 'var(--accent-blue)',
  completed: '#9C27B0',
  cancelled: 'var(--accent-red)',
};

/**
 * Public tournament listing page showing active and upcoming tournaments.
 */
export default function TournamentList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    setLoading(true);
    const statusParam = filter === 'completed' ? 'status=completed' : '';
    const url = statusParam
      ? `${API_URL}/api/tournaments?${statusParam}`
      : `${API_URL}/api/tournaments`;

    fetch(url)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const filtered = filter === 'active'
          ? data.filter((t) => !['draft', 'cancelled', 'completed'].includes(t.status))
          : data.filter((t) => t.status !== 'draft');
        setTournaments(filtered);
      })
      .catch(() => setTournaments([]))
      .finally(() => setLoading(false));
  }, [filter]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="tournament-page">
      <div className="tournament-page-header">
        <h1 className="tournament-page-title">{t('nav.tournaments')}</h1>
        <div className="tournament-filters">
          {[
            { key: 'active', labelKey: 'tournament.filterActive' },
            { key: 'completed', labelKey: 'tournament.filterCompleted' },
            { key: 'all', labelKey: 'matches.all' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`tournament-filter-btn ${filter === f.key ? 'active' : ''}`}
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {tournaments.length === 0 ? (
        <div className="tournament-empty">
          {t('tournament.empty')}
        </div>
      ) : (
        <div className="tournament-list">
          {tournaments.map((tour) => (
            <div
              key={tour.id}
              onClick={() => navigate(`/tournaments/${tour.id}`)}
              className="tournament-card lift-card notched-card"
            >
              <div>
                <div className="tournament-card-name">{tour.name}</div>
                <div className="tournament-card-meta">
                  <span>{tour.team_size}v{tour.team_size}</span>
                  <span>{tour.format === 'double_elim' ? t('tournament.doubleElim') : t('tournament.singleElim')}</span>
                  <span>{tour.match_type}</span>
                  <span>{t('tournament.registered', { count: tour.registration_count, max: tour.max_slots })}</span>
                  {tour.affects_elo && <span className="ranked">{t('tournament.ranked')}</span>}
                </div>
              </div>
              <div className="tournament-card-status">
                <div className="tournament-card-status-label" style={{ color: STATUS_COLORS[tour.status] }}>
                  {STATUS_LABELS[tour.status] ? t(STATUS_LABELS[tour.status]) : tour.status}
                </div>
                <div className="tournament-card-date">
                  {tour.starts_at
                    ? t('tournament.startPrefix', { date: new Date(tour.starts_at).toLocaleString(currentLocale()) })
                    : tour.registration_opens_at
                      ? t('tournament.regPrefix', { date: new Date(tour.registration_opens_at).toLocaleString(currentLocale()) })
                      : new Date(tour.created_at).toLocaleDateString(currentLocale())
                  }
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
